import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Camera, RotateCcw, Check, X } from 'lucide-react';

// Live camera (webcam on PC, front camera on phones) with snap / retake / use.
export const CameraCapture = ({ open, onClose, onCapture, title = 'Take photo' }) => {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [shot, setShot] = useState(null);
  const [error, setError] = useState('');
  const [ready, setReady] = useState(false);

  const stop = useCallback(() => { streamRef.current?.getTracks().forEach((t) => t.stop()); streamRef.current = null; }, []);
  const start = useCallback(async () => {
    setError(''); setShot(null); setReady(false);
    stop();
    try {
      const s = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 960 } }, audio: false });
      streamRef.current = s;
      if (videoRef.current) videoRef.current.srcObject = s;
    } catch (e) {
      setError(e?.name === 'NotAllowedError' ? 'Camera access was blocked. Allow camera access in your browser settings, or use Upload a file.' : 'No camera found on this device. Use Upload a file instead.');
    }
  }, []);

  useEffect(() => { if (open) start(); return stop; }, [open, start, stop]);
  useEffect(() => { if (!shot && videoRef.current && streamRef.current && videoRef.current.srcObject !== streamRef.current) videoRef.current.srcObject = streamRef.current; });

  const snap = () => {
    const v = videoRef.current;
    if (!v?.videoWidth) { setError('The camera is not ready yet. Please try again.'); return; }
    const c = document.createElement('canvas');
    c.width = v.videoWidth; c.height = v.videoHeight;
    c.getContext('2d').drawImage(v, 0, 0);
    c.toBlob((b) => (b ? setShot({ blob: b, url: URL.createObjectURL(b) }) : setError('Could not capture the photo. Please try again.')), 'image/jpeg', 0.92);
  };
  const use = () => { onCapture(new File([shot.blob], `broker-selfie-${Date.now()}.jpg`, { type: 'image/jpeg' })); stop(); onClose(); };
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" style={{ background: 'rgba(2,6,23,0.8)' }} data-testid="camera-capture">
      <div className="w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
          <p className="text-sm font-black text-slate-900">{title}</p>
          <button type="button" onClick={() => { stop(); onClose(); }} className="rounded-full p-1 text-slate-500 hover:bg-slate-100" data-testid="camera-capture-close"><X className="h-4 w-4" /></button>
        </div>
        <div className="relative aspect-[4/3]" style={{ background: '#0f172a' }}>
          {error ? <p className="absolute inset-0 grid place-items-center p-6 text-center text-sm" style={{ color: '#fff' }} data-testid="camera-capture-error">{error}</p>
            : shot ? <img src={shot.url} alt="Captured" className="h-full w-full object-cover" data-testid="camera-capture-preview" />
              : <video ref={videoRef} autoPlay playsInline muted onLoadedData={() => setReady(true)} className="h-full w-full -scale-x-100 object-cover" data-testid="camera-capture-video" />}
        </div>
        <div className="flex justify-center gap-2 p-4">
          {!shot ? (
            <button type="button" onClick={snap} disabled={!!error || !ready} data-testid="camera-capture-snap" style={{ background: '#0f172a', color: '#fff' }} className="inline-flex items-center gap-1.5 rounded-full px-5 py-2 text-sm font-bold disabled:opacity-40"><Camera className="h-4 w-4" /> {ready || error ? 'Take photo' : 'Starting camera…'}</button>
          ) : (<>
            <button type="button" onClick={start} data-testid="camera-capture-retake" className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 px-4 py-2 text-sm font-bold text-slate-700 hover:border-slate-500"><RotateCcw className="h-4 w-4" /> Retake</button>
            <button type="button" onClick={use} data-testid="camera-capture-use" className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600 px-5 py-2 text-sm font-bold text-white hover:bg-emerald-700"><Check className="h-4 w-4" /> Use this photo</button>
          </>)}
        </div>
      </div>
    </div>
  );
};
